import React from 'react'

import './css/Logo3D.css'

import { LOGO3D_MODEL, Logo3DModel } from './logo3dModel'

// The mark in three dimensions, for the hero: the rounded frame (the image)
// floating over the halftone grid of discs (the mark hidden under it), drawn
// with raw WebGL from the numbers extracted out of Logo3D.fbx by
// tools/logo3d/extract.mjs. No loader and no scene library: the geometry is a
// few hundred vertices rebuilt here from two outlines and a list of discs.
//
// It settles in from a flatter, lower pose on mount, like the shader behind
// it zooming in, then keeps a very slight sway, a degree or so on each axis
// over periods that never line up; the frame, floating above the discs,
// drifts against them as the model turns, which is what makes it read as two
// layers. It does not answer the pointer. A grain is printed over the discs,
// one cell per CSS pixel like the shader's dither, so they sit in the
// background's texture; the frame stays clean white, and reads as the solid
// thing above it.
//
// It only renders while near the viewport and the tab is visible, draws a
// single still frame under prefers-reduced-motion, and shows the flat PNG of
// the mark when WebGL is not available.

export interface Logo3DOptions {
	///@brief   Colour of the frame, before shading.
	frame: string
	///@brief   Colour of the discs on the left edge of the canvas...
	left: string
	///@brief   ...and on the right, the logo's blue-to-purple ramp.
	right: string
	///@brief   Amplitude of the sway, in degrees about the vertical axis;
	///         about the horizontal one it is a little less.
	sway: number
	///@brief   Depth of the grain printed over the discs: their brightness
	///         swings by half this either way from one pixel to the next;
	///         0 for none.
	grain: number
	///@brief   Grain cell size in CSS pixels, the shader behind the hero's
	///         dither cell, so both textures read as one.
	grainCell: number
	///@brief   Cap on the device pixel ratio the canvas renders at.
	maxDpr: number
}

const DEFAULT_OPTIONS: Logo3DOptions = {
	frame: '#FAFAFA',
	// Sampled off the flat PNG of the mark, so the hero keeps its colours.
	left: '#87B3FF',
	right: '#BB8FFF',
	sway: 0.8,
	grain: 0.26,
	grainCell: 1,
	maxDpr: 2,
}

///@brief   Share of the canvas the model fills at rest, the remainder
///         being the room it needs to sway and settle in without touching
///         the edges.
const FIT: number = 0.94
///@brief   Duration of the entrance, in seconds.
const INTRO_DURATION: number = 1.4
///@brief   Extra lean at the start of the entrance, in degrees.
const INTRO_LEAN: number = -6
///@brief   Rise over the entrance, as a share of the canvas height.
const INTRO_RISE: number = 0.05
///@brief   Smallest radius a disc is drawn at, in CSS pixels: below it the
///         disc is enlarged and dimmed instead of flickering away.
const MIN_RADIUS_PX: number = 0.8

type RGB = [ number, number, number ]

function parseColor( s: string, fallback: RGB ): RGB {
	const hex: RegExpExecArray | null = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec( s.trim() )
	if ( hex !== null ) {
		return [ parseInt( hex[ 1 ], 16 ) / 255, parseInt( hex[ 2 ], 16 ) / 255, parseInt( hex[ 3 ], 16 ) / 255 ]
	}
	const rgb: RegExpExecArray | null = /^rgba?\s*\(\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)/i.exec( s.trim() )
	if ( rgb !== null ) {
		return [ parseFloat( rgb[ 1 ] ) / 255, parseFloat( rgb[ 2 ] ) / 255, parseFloat( rgb[ 3 ] ) / 255 ]
	}
	return fallback
}

function easeOutCubic( t: number ): number {
	const u: number = 1 - t
	return 1 - u * u * u
}


// --- 3x3 matrices, column-major as GLSL wants them. ---

type Mat3 = number[]

function rotationX( degrees: number ): Mat3 {
	const a: number = degrees * Math.PI / 180
	const c: number = Math.cos( a )
	const s: number = Math.sin( a )
	return [ 1, 0, 0, 0, c, s, 0, -s, c ]
}

function rotationY( degrees: number ): Mat3 {
	const a: number = degrees * Math.PI / 180
	const c: number = Math.cos( a )
	const s: number = Math.sin( a )
	return [ c, 0, -s, 0, 1, 0, s, 0, c ]
}

function rotationZ( degrees: number ): Mat3 {
	const a: number = degrees * Math.PI / 180
	const c: number = Math.cos( a )
	const s: number = Math.sin( a )
	return [ c, s, 0, -s, c, 0, 0, 0, 1 ]
}

function multiply( a: Mat3, b: Mat3 ): Mat3 {
	const out: Mat3 = new Array( 9 )
	for ( let j: number = 0; j < 3; j++ ) {
		for ( let i: number = 0; i < 3; i++ ) {
			out[ j * 3 + i ] = a[ i ] * b[ j * 3 ] + a[ 3 + i ] * b[ j * 3 + 1 ] + a[ 6 + i ] * b[ j * 3 + 2 ]
		}
	}
	return out
}

function transform( m: Mat3, x: number, y: number, z: number ): [ number, number, number ] {
	return [
		m[ 0 ] * x + m[ 3 ] * y + m[ 6 ] * z,
		m[ 1 ] * x + m[ 4 ] * y + m[ 7 ] * z,
		m[ 2 ] * x + m[ 5 ] * y + m[ 8 ] * z,
	]
}


// --- Geometry, rebuilt from the model's outlines and discs. ---

interface Geometry {
	vertices: Float32Array
	indices: Uint16Array
}

///@brief   The frame as a solid ring: top and underside between the two
///         outlines, an outer wall and an inner wall, each face flat-shaded
///         with its own normal. Position and normal interleaved.
function buildFrame( model: Logo3DModel ): Geometry {
	const count: number = Math.min( model.frameOuter.length, model.frameInner.length ) / 2
	const vertices: number[] = []
	const indices: number[] = []

	const push = ( x: number, y: number, z: number, n: [ number, number, number ] ): void => {
		vertices.push( x, y, z, n[ 0 ], n[ 1 ], n[ 2 ] )
	}
	const quad = ( p: [ number, number, number ][], n: [ number, number, number ] ): void => {
		const base: number = vertices.length / 6
		for ( let i: number = 0; i < 4; i++ ) push( p[ i ][ 0 ], p[ i ][ 1 ], p[ i ][ 2 ], n )
		indices.push( base, base + 1, base + 2, base, base + 2, base + 3 )
	}

	const zb: number = model.frameBottom
	const zt: number = model.frameTop
	for ( let i: number = 0; i < count; i++ ) {
		const j: number = ( i + 1 ) % count
		const ox0: number = model.frameOuter[ i * 2 ]
		const oy0: number = model.frameOuter[ i * 2 + 1 ]
		const ox1: number = model.frameOuter[ j * 2 ]
		const oy1: number = model.frameOuter[ j * 2 + 1 ]
		const ix0: number = model.frameInner[ i * 2 ]
		const iy0: number = model.frameInner[ i * 2 + 1 ]
		const ix1: number = model.frameInner[ j * 2 ]
		const iy1: number = model.frameInner[ j * 2 + 1 ]

		quad( [ [ ox0, oy0, zt ], [ ox1, oy1, zt ], [ ix1, iy1, zt ], [ ix0, iy0, zt ] ], [ 0, 0, 1 ] )
		quad( [ [ ox0, oy0, zb ], [ ix0, iy0, zb ], [ ix1, iy1, zb ], [ ox1, oy1, zb ] ], [ 0, 0, -1 ] )

		// The outlines run counter-clockwise, so the outward side of a
		// segment is to its right.
		let dx: number = ox1 - ox0
		let dy: number = oy1 - oy0
		let len: number = Math.hypot( dx, dy ) || 1
		quad( [ [ ox0, oy0, zb ], [ ox1, oy1, zb ], [ ox1, oy1, zt ], [ ox0, oy0, zt ] ], [ dy / len, -dx / len, 0 ] )

		dx = ix1 - ix0
		dy = iy1 - iy0
		len = Math.hypot( dx, dy ) || 1
		quad( [ [ ix0, iy0, zb ], [ ix0, iy0, zt ], [ ix1, iy1, zt ], [ ix1, iy1, zb ] ], [ -dy / len, dx / len, 0 ] )
	}

	return { vertices: new Float32Array( vertices ), indices: new Uint16Array( indices ) }
}

///@brief   One square per disc, in the plane of the discs; the disc itself
///         is cut out of it in the fragment shader. Centre, corner and
///         radius interleaved.
function buildDots( model: Logo3DModel ): Geometry {
	const count: number = model.dots.length / 3
	const vertices: number[] = []
	const indices: number[] = []
	const corners: [ number, number ][] = [ [ -1, -1 ], [ 1, -1 ], [ 1, 1 ], [ -1, 1 ] ]

	for ( let i: number = 0; i < count; i++ ) {
		const x: number = model.dots[ i * 3 ]
		const y: number = model.dots[ i * 3 + 1 ]
		const r: number = model.dots[ i * 3 + 2 ] / 2
		const base: number = i * 4
		for ( let c: number = 0; c < 4; c++ ) vertices.push( x, y, corners[ c ][ 0 ], corners[ c ][ 1 ], r )
		indices.push( base, base + 1, base + 2, base, base + 2, base + 3 )
	}

	return { vertices: new Float32Array( vertices ), indices: new Uint16Array( indices ) }
}

interface Bounds {
	x: number
	y: number
	width: number
	height: number
}

///@brief   Extent of the model on screen at rest, from every corner of the
///         frame and of the discs' squares, to centre and fit it.
function measure( model: Logo3DModel, pose: Mat3 ): Bounds {
	let minX: number = Infinity
	let minY: number = Infinity
	let maxX: number = -Infinity
	let maxY: number = -Infinity
	const take = ( x: number, y: number, z: number ): void => {
		const p: [ number, number, number ] = transform( pose, x, y, z )
		if ( p[ 0 ] < minX ) minX = p[ 0 ]
		if ( p[ 0 ] > maxX ) maxX = p[ 0 ]
		if ( p[ 1 ] < minY ) minY = p[ 1 ]
		if ( p[ 1 ] > maxY ) maxY = p[ 1 ]
	}
	for ( let i: number = 0; i < model.frameOuter.length; i += 2 ) {
		take( model.frameOuter[ i ], model.frameOuter[ i + 1 ], model.frameBottom )
		take( model.frameOuter[ i ], model.frameOuter[ i + 1 ], model.frameTop )
	}
	for ( let i: number = 0; i < model.dots.length; i += 3 ) {
		const r: number = model.dots[ i + 2 ] / 2
		take( model.dots[ i ] - r, model.dots[ i + 1 ] - r, 0 )
		take( model.dots[ i ] + r, model.dots[ i + 1 ] - r, 0 )
		take( model.dots[ i ] + r, model.dots[ i + 1 ] + r, 0 )
		take( model.dots[ i ] - r, model.dots[ i + 1 ] + r, 0 )
	}
	return { x: ( minX + maxX ) / 2, y: ( minY + maxY ) / 2, width: maxX - minX, height: maxY - minY }
}


// --- Shaders. ---

// Shared by both programs: the model turned by the pose, then an orthographic
// projection centred on the model's rest extent. Depth runs along z toward
// the viewer, so the frame covers the discs under it.
const PROJECTION: string = `
uniform mat3 u_rotation;
uniform vec2 u_center;
uniform vec2 u_scale;
uniform vec2 u_offset;
uniform float u_depth;

vec4 project( vec3 world ) {
	return vec4( ( world.xy - u_center ) * u_scale + u_offset, -world.z * u_depth, 1.0 );
}
`

const FRAME_VERTEX: string = PROJECTION + `
attribute vec3 a_position;
attribute vec3 a_normal;
varying vec3 v_normal;

void main() {
	v_normal = u_rotation * a_normal;
	gl_Position = project( u_rotation * a_position );
}
`

// The grain, for the discs: a random value per cell, the cells being CSS
// pixels like the dither cells of the shader behind the hero, swings each
// pixel's brightness a little either way. Centred on zero, so the discs stay
// as bright on average as without it. Screen-space, so it stays put as the
// model turns, the way the shader's dither does over its drifting pattern; an
// ordered threshold was tried first and read as a printed screen rather than
// as grain.
const GRAIN: string = `
uniform float u_cell;
uniform float u_grain;

// Dave Hoskins' hash12, stable on GPUs where the usual sin hash breaks up.
float hash( vec2 p ) {
	vec3 p3 = fract( vec3( p.xyx ) * 0.1031 );
	p3 += dot( p3, p3.yzx + 33.33 );
	return fract( ( p3.x + p3.y ) * p3.z );
}

vec3 grain( vec3 col ) {
	float noise = hash( floor( gl_FragCoord.xy / u_cell ) );
	return col * ( 1.0 + u_grain * ( noise - 0.5 ) );
}
`

const FRAME_FRAGMENT: string = `
precision highp float;

uniform vec3 u_color;
uniform vec3 u_light;
varying vec3 v_normal;

void main() {
	vec3 n = normalize( v_normal );
	float diffuse = max( dot( n, u_light ), 0.0 );
	// The top face turns toward the light and stays near white; the walls
	// fall away from it and read as the frame's thickness.
	float shade = 0.5 + 0.42 * diffuse + 0.08 * max( n.z, 0.0 );
	gl_FragColor = vec4( u_color * shade, 1.0 );
}
`

const DOTS_VERTEX: string = PROJECTION + `
attribute vec2 a_center;
attribute vec2 a_corner;
attribute float a_radius;
uniform float u_minRadius;
uniform float u_pxPerUnit;
varying vec2 v_uv;
varying vec2 v_ndc;
varying float v_cover;
varying float v_edge;

void main() {
	// The smallest discs are drawn larger and dimmer rather than as a flicker
	// of sub-pixel specks: the same amount of light, spread over more pixels.
	float r = max( a_radius, u_minRadius );
	v_cover = ( a_radius * a_radius ) / ( r * r );
	v_uv = a_corner;
	v_edge = 1.0 / ( r * u_pxPerUnit );
	vec4 p = project( u_rotation * vec3( a_center + a_corner * r, 0.0 ) );
	v_ndc = p.xy;
	gl_Position = p;
}
`

const DOTS_FRAGMENT: string = `
precision highp float;
` + GRAIN + `
uniform vec3 u_left;
uniform vec3 u_right;
varying vec2 v_uv;
varying vec2 v_ndc;
varying float v_cover;
varying float v_edge;

void main() {
	float r = length( v_uv );
#ifdef HAS_DERIVATIVES
	float w = max( fwidth( r ), 0.002 );
#else
	float w = v_edge * 2.4;
#endif
	float a = ( 1.0 - smoothstep( 1.0 - w, 1.0, r ) ) * v_cover;

	// Blue on the left, purple on the right, across the whole canvas.
	vec3 col = grain( mix( u_left, u_right, smoothstep( -0.9, 0.9, v_ndc.x ) ) );

	gl_FragColor = vec4( col * a, a );
}
`

function compileShader( gl: WebGLRenderingContext, type: number, source: string ): WebGLShader | null {
	const shader: WebGLShader | null = gl.createShader( type )
	if ( shader === null ) return null
	gl.shaderSource( shader, source )
	gl.compileShader( shader )
	if ( !gl.getShaderParameter( shader, gl.COMPILE_STATUS ) ) {
		console.warn( 'Logo3D: shader compilation failed', gl.getShaderInfoLog( shader ) )
		gl.deleteShader( shader )
		return null
	}
	return shader
}

function linkProgram( gl: WebGLRenderingContext, vertexSource: string, fragmentSource: string ): WebGLProgram | null {
	const vertex: WebGLShader | null = compileShader( gl, gl.VERTEX_SHADER, vertexSource )
	const fragment: WebGLShader | null = compileShader( gl, gl.FRAGMENT_SHADER, fragmentSource )
	if ( vertex === null || fragment === null ) return null
	const program: WebGLProgram | null = gl.createProgram()
	if ( program === null ) return null
	gl.attachShader( program, vertex )
	gl.attachShader( program, fragment )
	gl.linkProgram( program )
	gl.deleteShader( vertex )
	gl.deleteShader( fragment )
	if ( !gl.getProgramParameter( program, gl.LINK_STATUS ) ) {
		console.warn( 'Logo3D: program link failed', gl.getProgramInfoLog( program ) )
		gl.deleteProgram( program )
		return null
	}
	return program
}

interface Uniforms {
	[ name: string ]: WebGLUniformLocation | null
}

///@brief   One drawable: its program, buffers and uniform locations.
interface Pass {
	program: WebGLProgram
	vertices: WebGLBuffer
	indices: WebGLBuffer
	count: number
	uniforms: Uniforms
}


///@brief   The model in one canvas filling its host element.
class LogoScene {

	private element: HTMLElement
	private canvas: HTMLCanvasElement
	private gl: WebGLRenderingContext | null = null
	private frame: Pass | null = null
	private dots: Pass | null = null

	private options: Logo3DOptions
	private model: Logo3DModel
	///@brief   The model's own pose: spun in its plane, then leant back.
	private rest: Mat3
	private bounds: Bounds

	private width: number = 0
	private height: number = 0
	private dpr: number = 1
	///@brief   Device pixels per model unit at the current size.
	private pxPerUnit: number = 1

	private bRunning: boolean = false
	private bVisible: boolean = true
	private bReducedMotion: boolean = false
	private bContextLost: boolean = false
	private bReady: boolean = false
	private rafId: number | null = null

	///@brief   Origin of the clock and of the entrance, in ms.
	private startTime: number = 0
	///@brief   Phase offset so that each visit sways from a different point.
	private seed: number = Math.random() * 100

	private resizeObserver: ResizeObserver | null = null
	private intersectionObserver: IntersectionObserver | null = null
	private reducedMotionQuery: MediaQueryList | null = null

	constructor( element: HTMLElement, options: Logo3DOptions, model: Logo3DModel ) {
		this.element = element
		this.options = options
		this.model = model
		this.rest = multiply( rotationX( model.tilt ), rotationZ( model.spin ) )
		this.bounds = measure( model, this.rest )

		element.classList.remove( 'is-fallback' )
		const previous: HTMLCanvasElement | null = element.querySelector( 'canvas' )
		if ( previous !== null ) element.removeChild( previous )

		this.canvas = document.createElement( 'canvas' )
		element.appendChild( this.canvas )

		this.canvas.addEventListener( 'webglcontextlost', this )
		this.canvas.addEventListener( 'webglcontextrestored', this )

		if ( !this.initGl() ) {
			element.classList.add( 'is-fallback' )
			element.removeChild( this.canvas )
			return
		}

		if ( typeof window.matchMedia === 'function' ) {
			this.reducedMotionQuery = window.matchMedia( '(prefers-reduced-motion: reduce)' )
			this.bReducedMotion = this.reducedMotionQuery.matches
			this.reducedMotionQuery.addEventListener( 'change', this )
		}

		if ( typeof ResizeObserver !== 'undefined' ) {
			this.resizeObserver = new ResizeObserver( (): void => { this.onResize() } )
			this.resizeObserver.observe( element )
		} else {
			window.addEventListener( 'resize', this )
		}

		// Only render while the logo is near the viewport; the margin keeps
		// it from flipping on and off with every small scroll at the edge.
		if ( typeof IntersectionObserver !== 'undefined' ) {
			this.intersectionObserver = new IntersectionObserver( ( entries: IntersectionObserverEntry[] ): void => {
				entries.forEach( ( entry: IntersectionObserverEntry ): void => {
					this.bVisible = entry.isIntersecting
				} )
				this.syncLoop()
			}, { rootMargin: '30% 0px 30% 0px' } )
			this.intersectionObserver.observe( element )
		}

		document.addEventListener( 'visibilitychange', this )

		this.startTime = performance.now()
		this.updateSize()
	}

	///@brief   Creates the context, the two programs and their geometry.
	///         False when WebGL is unavailable, in which case the host shows
	///         its flat fallback.
	private initGl(): boolean {
		let gl: WebGLRenderingContext | null = null
		const attributes: WebGLContextAttributes = { alpha: true, antialias: true, depth: true, stencil: false, premultipliedAlpha: true, preserveDrawingBuffer: false }
		try {
			gl = this.canvas.getContext( 'webgl', attributes )
				|| ( this.canvas.getContext( 'experimental-webgl', attributes ) as WebGLRenderingContext | null )
		} catch ( e ) {
			gl = null
		}
		if ( gl === null ) return false

		// Smooth disc edges need the pixel footprint; without the extension
		// the vertex shader's estimate stands in.
		const derivatives: boolean = gl.getExtension( 'OES_standard_derivatives' ) !== null
		const prefix: string = derivatives ? '#extension GL_OES_standard_derivatives : enable\n#define HAS_DERIVATIVES\n' : ''

		const frameProgram: WebGLProgram | null = linkProgram( gl, FRAME_VERTEX, FRAME_FRAGMENT )
		const dotsProgram: WebGLProgram | null = linkProgram( gl, DOTS_VERTEX, prefix + DOTS_FRAGMENT )
		if ( frameProgram === null || dotsProgram === null ) return false

		const frame: Pass | null = this.upload( gl, frameProgram, buildFrame( this.model ), [ 'u_rotation', 'u_center', 'u_scale', 'u_offset', 'u_depth', 'u_color', 'u_light' ] )
		const dots: Pass | null = this.upload( gl, dotsProgram, buildDots( this.model ), [ 'u_rotation', 'u_center', 'u_scale', 'u_offset', 'u_depth', 'u_minRadius', 'u_pxPerUnit', 'u_left', 'u_right', 'u_cell', 'u_grain' ] )
		if ( frame === null || dots === null ) return false

		gl.useProgram( frame.program )
		gl.uniform3fv( frame.uniforms[ 'u_color' ], parseColor( this.options.frame, [ 0.98, 0.98, 0.98 ] ) )
		// From above, to the left and toward the viewer.
		const light: [ number, number, number ] = [ -0.35, 0.75, 0.55 ]
		const norm: number = Math.hypot( light[ 0 ], light[ 1 ], light[ 2 ] )
		gl.uniform3f( frame.uniforms[ 'u_light' ], light[ 0 ] / norm, light[ 1 ] / norm, light[ 2 ] / norm )
		gl.uniform1f( frame.uniforms[ 'u_depth' ], 1 / 200 )

		gl.useProgram( dots.program )
		gl.uniform3fv( dots.uniforms[ 'u_left' ], parseColor( this.options.left, [ 0.53, 0.7, 1 ] ) )
		gl.uniform3fv( dots.uniforms[ 'u_right' ], parseColor( this.options.right, [ 0.73, 0.56, 1 ] ) )
		gl.uniform1f( dots.uniforms[ 'u_depth' ], 1 / 200 )
		gl.uniform1f( dots.uniforms[ 'u_grain' ], this.options.grain )

		gl.enable( gl.DEPTH_TEST )
		gl.depthFunc( gl.LESS )
		gl.disable( gl.CULL_FACE )
		gl.enable( gl.BLEND )
		// The discs come out premultiplied.
		gl.blendFunc( gl.ONE, gl.ONE_MINUS_SRC_ALPHA )
		gl.clearColor( 0, 0, 0, 0 )

		this.gl = gl
		this.frame = frame
		this.dots = dots
		this.bContextLost = false
		return true
	}

	private upload( gl: WebGLRenderingContext, program: WebGLProgram, geometry: Geometry, uniformNames: string[] ): Pass | null {
		const vertices: WebGLBuffer | null = gl.createBuffer()
		const indices: WebGLBuffer | null = gl.createBuffer()
		if ( vertices === null || indices === null ) return null
		gl.bindBuffer( gl.ARRAY_BUFFER, vertices )
		gl.bufferData( gl.ARRAY_BUFFER, geometry.vertices, gl.STATIC_DRAW )
		gl.bindBuffer( gl.ELEMENT_ARRAY_BUFFER, indices )
		gl.bufferData( gl.ELEMENT_ARRAY_BUFFER, geometry.indices, gl.STATIC_DRAW )
		const uniforms: Uniforms = {}
		for ( let i: number = 0; i < uniformNames.length; i++ ) {
			uniforms[ uniformNames[ i ] ] = gl.getUniformLocation( program, uniformNames[ i ] )
		}
		return { program, vertices, indices, count: geometry.indices.length, uniforms }
	}

	handleEvent( e: Event ): void {
		switch ( e.type ) {
			case 'resize':
				this.onResize()
				break
			case 'visibilitychange':
				this.syncLoop()
				break
			case 'change':
				this.bReducedMotion = this.reducedMotionQuery !== null && this.reducedMotionQuery.matches
				this.syncLoop()
				this.drawFrame( performance.now() )
				break
			case 'webglcontextlost':
				e.preventDefault()
				this.bContextLost = true
				this.stop()
				break
			case 'webglcontextrestored':
				if ( this.initGl() ) {
					this.updateSize()
					this.syncLoop()
				}
				break
		}
	}

	private onResize(): void {
		this.updateSize()
		this.drawFrame( performance.now() )
	}

	private updateSize(): void {
		this.width = this.element.clientWidth
		this.height = this.element.clientHeight
		this.dpr = Math.min( window.devicePixelRatio || 1, this.options.maxDpr )

		const w: number = Math.max( 1, Math.round( this.width * this.dpr ) )
		const h: number = Math.max( 1, Math.round( this.height * this.dpr ) )
		this.canvas.width = w
		this.canvas.height = h
		this.canvas.style.width = this.width + 'px'
		this.canvas.style.height = this.height + 'px'

		// Fit the rest pose into the canvas, whichever side binds.
		this.pxPerUnit = Math.min( w * FIT / this.bounds.width, h * FIT / this.bounds.height )

		const gl: WebGLRenderingContext | null = this.gl
		if ( gl === null || this.bContextLost || this.frame === null || this.dots === null ) return
		gl.viewport( 0, 0, w, h )

		const scaleX: number = this.pxPerUnit * 2 / w
		const scaleY: number = this.pxPerUnit * 2 / h
		gl.useProgram( this.frame.program )
		gl.uniform2f( this.frame.uniforms[ 'u_center' ], this.bounds.x, this.bounds.y )
		gl.uniform2f( this.frame.uniforms[ 'u_scale' ], scaleX, scaleY )
		gl.useProgram( this.dots.program )
		gl.uniform2f( this.dots.uniforms[ 'u_center' ], this.bounds.x, this.bounds.y )
		gl.uniform2f( this.dots.uniforms[ 'u_scale' ], scaleX, scaleY )
		// Grain cells are sized in CSS pixels, like the shader's dither, so
		// the two textures match on every screen density.
		gl.uniform1f( this.dots.uniforms[ 'u_cell' ], Math.max( 1, Math.round( this.options.grainCell * this.dpr ) ) )
		gl.uniform1f( this.dots.uniforms[ 'u_pxPerUnit' ], this.pxPerUnit )
		gl.uniform1f( this.dots.uniforms[ 'u_minRadius' ], MIN_RADIUS_PX * this.dpr / this.pxPerUnit )
	}

	private drawFrame( now: number ): void {
		const gl: WebGLRenderingContext | null = this.gl
		if ( gl === null || this.bContextLost || this.frame === null || this.dots === null ) return

		let yaw: number = 0
		let pitch: number = 0
		let rise: number = 0
		if ( !this.bReducedMotion ) {
			const elapsed: number = ( now - this.startTime ) * 0.001
			const intro: number = easeOutCubic( Math.min( 1, elapsed / INTRO_DURATION ) )
			const t: number = elapsed + this.seed

			// A slow sway, two incommensurate periods so it never loops.
			yaw = this.options.sway * Math.sin( t * 0.37 )
			pitch = this.options.sway * 0.7 * Math.sin( t * 0.29 + 1.3 )
			// Entrance: settles up from a flatter, lower pose.
			pitch += INTRO_LEAN * ( 1 - intro )
			rise = -INTRO_RISE * 2 * ( 1 - intro )
		}

		const pose: Mat3 = multiply( multiply( rotationY( yaw ), rotationX( pitch ) ), this.rest )

		gl.clear( gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT )

		// The frame first, writing depth, so the discs under it stay hidden.
		const frame: Pass = this.frame
		gl.useProgram( frame.program )
		gl.uniformMatrix3fv( frame.uniforms[ 'u_rotation' ], false, pose )
		gl.uniform2f( frame.uniforms[ 'u_offset' ], 0, rise )
		gl.depthMask( true )
		gl.bindBuffer( gl.ARRAY_BUFFER, frame.vertices )
		gl.bindBuffer( gl.ELEMENT_ARRAY_BUFFER, frame.indices )
		const framePosition: number = gl.getAttribLocation( frame.program, 'a_position' )
		const frameNormal: number = gl.getAttribLocation( frame.program, 'a_normal' )
		gl.enableVertexAttribArray( framePosition )
		gl.vertexAttribPointer( framePosition, 3, gl.FLOAT, false, 24, 0 )
		gl.enableVertexAttribArray( frameNormal )
		gl.vertexAttribPointer( frameNormal, 3, gl.FLOAT, false, 24, 12 )
		gl.drawElements( gl.TRIANGLES, frame.count, gl.UNSIGNED_SHORT, 0 )
		gl.disableVertexAttribArray( framePosition )
		gl.disableVertexAttribArray( frameNormal )

		// Then the discs, blended over the black, tested against the frame.
		const dots: Pass = this.dots
		gl.useProgram( dots.program )
		gl.uniformMatrix3fv( dots.uniforms[ 'u_rotation' ], false, pose )
		gl.uniform2f( dots.uniforms[ 'u_offset' ], 0, rise )
		gl.depthMask( false )
		gl.bindBuffer( gl.ARRAY_BUFFER, dots.vertices )
		gl.bindBuffer( gl.ELEMENT_ARRAY_BUFFER, dots.indices )
		const dotCenter: number = gl.getAttribLocation( dots.program, 'a_center' )
		const dotCorner: number = gl.getAttribLocation( dots.program, 'a_corner' )
		const dotRadius: number = gl.getAttribLocation( dots.program, 'a_radius' )
		gl.enableVertexAttribArray( dotCenter )
		gl.vertexAttribPointer( dotCenter, 2, gl.FLOAT, false, 20, 0 )
		gl.enableVertexAttribArray( dotCorner )
		gl.vertexAttribPointer( dotCorner, 2, gl.FLOAT, false, 20, 8 )
		gl.enableVertexAttribArray( dotRadius )
		gl.vertexAttribPointer( dotRadius, 1, gl.FLOAT, false, 20, 16 )
		gl.drawElements( gl.TRIANGLES, dots.count, gl.UNSIGNED_SHORT, 0 )
		gl.disableVertexAttribArray( dotCenter )
		gl.disableVertexAttribArray( dotCorner )
		gl.disableVertexAttribArray( dotRadius )

		if ( !this.bReady ) {
			this.bReady = true
			this.canvas.classList.add( 'is-ready' )
		}
	}

	private animate( timestamp: number ): void {
		if ( !this.bRunning ) {
			this.rafId = null
			return
		}

		this.drawFrame( timestamp )

		this.rafId = window.requestAnimationFrame( ( ts: number ): void => { this.animate( ts ) } )
	}

	private syncLoop(): void {
		const bShouldRun: boolean = this.bVisible && !document.hidden && !this.bReducedMotion && !this.bContextLost
		if ( bShouldRun ) {
			this.start()
		} else {
			this.stop()
		}
	}

	public start(): void {
		if ( this.bRunning || this.gl === null ) return
		if ( !this.bVisible || document.hidden || this.bReducedMotion || this.bContextLost ) {
			this.drawFrame( performance.now() )
			return
		}
		this.bRunning = true
		this.rafId = window.requestAnimationFrame( ( ts: number ): void => { this.animate( ts ) } )
	}

	public stop(): void {
		this.bRunning = false
		if ( this.rafId !== null ) {
			window.cancelAnimationFrame( this.rafId )
			this.rafId = null
		}
	}

	public destroy(): void {
		this.stop()
		if ( this.resizeObserver !== null ) this.resizeObserver.disconnect()
		if ( this.intersectionObserver !== null ) this.intersectionObserver.disconnect()
		if ( this.reducedMotionQuery !== null ) this.reducedMotionQuery.removeEventListener( 'change', this )
		window.removeEventListener( 'resize', this )
		document.removeEventListener( 'visibilitychange', this )
		this.canvas.removeEventListener( 'webglcontextlost', this )
		this.canvas.removeEventListener( 'webglcontextrestored', this )

		const gl: WebGLRenderingContext | null = this.gl
		if ( gl !== null ) {
			const passes: ( Pass | null )[] = [ this.frame, this.dots ]
			for ( let i: number = 0; i < passes.length; i++ ) {
				const pass: Pass | null = passes[ i ]
				if ( pass === null ) continue
				gl.deleteBuffer( pass.vertices )
				gl.deleteBuffer( pass.indices )
				gl.deleteProgram( pass.program )
			}
			const lose = gl.getExtension( 'WEBGL_lose_context' )
			if ( lose !== null ) lose.loseContext()
			this.gl = null
			this.frame = null
			this.dots = null
		}
		if ( this.canvas.parentNode === this.element ) this.element.removeChild( this.canvas )
	}

}


export interface Logo3DProps extends Partial<Logo3DOptions> {
	className?: string
	///@brief   Flat picture of the mark, shown instead when WebGL is not
	///         available.
	fallbackSrc?: string
}

///@brief   The mark in three dimensions, filling its host's width at a 2:1
///         aspect. Purely visual: hidden from assistive tech and transparent
///         to the mouse, which it does not follow either.
export function Logo3D( props: Logo3DProps ): React.JSX.Element {

	const {
		className,
		fallbackSrc,
		frame = DEFAULT_OPTIONS.frame,
		left = DEFAULT_OPTIONS.left,
		right = DEFAULT_OPTIONS.right,
		sway = DEFAULT_OPTIONS.sway,
		grain = DEFAULT_OPTIONS.grain,
		grainCell = DEFAULT_OPTIONS.grainCell,
		maxDpr = DEFAULT_OPTIONS.maxDpr,
	} = props

	const ref = React.useRef<HTMLDivElement>( null )

	React.useEffect( (): ( () => void ) | undefined => {
		const element: HTMLDivElement | null = ref.current
		if ( element === null ) return undefined

		const scene: LogoScene = new LogoScene( element, { frame, left, right, sway, grain, grainCell, maxDpr }, LOGO3D_MODEL )
		scene.start()

		return (): void => { scene.destroy() }
	}, [ frame, left, right, sway, grain, grainCell, maxDpr ] )

	return (
		<div ref={ ref } className={ `Logo3D${ className ? ' ' + className : '' }` } aria-hidden="true" >
			{ fallbackSrc !== undefined && <img className="Logo3D-Fallback" src={ fallbackSrc } alt="" /> }
		</div>
	)
}
